import { getApolloClient } from '../lib/getApolloClient';
import {
  GET_STATS_FOR_CURRENT_USER,
  GET_CURRENT_USER,
  GET_USERS_BY_TENANT,
  GET_WORKERS_LIST_BY_TENANT_PAGINATED,
  GET_USER,
  GET_USERS_BY_CLIENT,
  GET_WORKERS_BY_CLIENT,
  UPDATE_USER,
  UPDATE_USER_ASSIGNED_ADMIN,
  UPDATE_USER_STATUS,
  ASSIGN_CLASSIFICATION_LEVEL_TO_USER,
  REMOVE_CLASSIFICATION_LEVEL_FROM_USER,
  INITIATE_USER_SELF_DELETE,
  GET_USER_DETAILED,
  CLEAR_USER_FCM_TOKEN,
  VERIFY_CURRENT_USER_PASSWORD,
  GET_SUPER_DETAILS_FOR_CURRENT_USER,
  GET_BANK_AND_TAX_DETAILS_FOR_CURRENT_USER,
} from './user.gql';
import {
  CustomAttribute,
  PaginatedUserResponse,
  User,
  UserRole,
  UserInput,
  UserStatus,
} from '../generated/gql-types';
import { handleGraphQLErrors } from '../util/graphqlError.util';

export const fetchStatsForCurrentUser = async () => {
  try {
    const { data } = await getApolloClient().query({
      query: GET_STATS_FOR_CURRENT_USER,
      fetchPolicy: 'cache-first',
    });
    if (data.getStatsForCurrentUser) {
      return data.getStatsForCurrentUser;
    }
    return null;
  } catch (error) {
    console.error('Error fetching stats for current user:', error);
    return null;
  }
};

export const fetchCurrentUser = async (): Promise<User | null> => {
  try {
    const { data } = await getApolloClient().query({
      query: GET_CURRENT_USER,
    });
    return data.getCurrentUser;
  } catch (error) {
    // Check if it's an Apollo network error with status code 500
    if (
      error instanceof Error &&
      'networkError' in error &&
      (error as any).networkError?.statusCode === 500
    ) {
      throw new Error(
        'Server error: Unable to fetch user data. Please try again later.',
      );
    }
    throw error;
  }
};

export const fetchCurrentUserOnMobile = async (options?: {
  networkOnly?: boolean;
}): Promise<User | null> => {
  try {
    const { data } = await getApolloClient().query({
      query: GET_CURRENT_USER,
      fetchPolicy: options?.networkOnly ? 'network-only' : 'cache-first',
    });
    return data.getCurrentUser;
  } catch (error) {
    console.error('Error fetching current user:', error);
    throw error;
  }
};

export const fetchUsersByTenant = async (
  tenantId: string,
  roles?: UserRole[] | null,
): Promise<User[]> => {
  try {
    const { data } = await getApolloClient().query({
      query: GET_USERS_BY_TENANT,
      variables: {
        tenantId,
        roles: roles ?? undefined,
      },
    });
    return data.getUsersByTenantId;
  } catch (error) {
    console.error('Error fetching users by tenant:', error);
    return [];
  }
};

// Lightweight, paginated workers-by-tenant fetch backing the Workers list page.
// Returns only the fields needed by the list (see WORKER_LIST_FRAGMENT) so
// large tenants with thousands of workers paint significantly faster. Fetching
// every worker in one request exceeds the maximum response size, hence the page
// based approach.
export const fetchWorkersListByTenantPaginated = async (
  tenantId: string,
  roles?: UserRole[] | null,
  limit?: number,
  lastEvaluatedKey?: string,
): Promise<PaginatedUserResponse> => {
  try {
    const { data } = await getApolloClient().query({
      query: GET_WORKERS_LIST_BY_TENANT_PAGINATED,
      variables: {
        tenantId,
        roles: roles ?? undefined,
        ...(limit && { limit }),
        ...(lastEvaluatedKey && { lastEvaluatedKey }),
      },
    });
    const paginatedUsers = data?.getUsersByTenantIdPaginated;
    return {
      users: paginatedUsers?.users ?? [],
      hasMore: paginatedUsers?.hasMore ?? false,
      lastEvaluatedKey: paginatedUsers?.lastEvaluatedKey ?? null,
    };
  } catch (error) {
    console.error('Error fetching workers list by tenant:', error);
    return {
      users: [],
      hasMore: false,
      lastEvaluatedKey: null,
    };
  }
};

export const fetchTenantUsersForTenant = async (
  tenantId: string,
): Promise<User[]> => {
  try {
    return await fetchUsersByTenant(tenantId, [
      UserRole.TenantAdmin,
      UserRole.TenantUser,
    ]);
  } catch (error) {
    console.error('Error fetching tenant users:', error);
    return [];
  }
};

export const fetchUsersByClient = async (clientId: string): Promise<User[]> => {
  try {
    const { data } = await getApolloClient().query({
      query: GET_USERS_BY_CLIENT,
      variables: { clientId },
    });
    return data.getUsersByClientId;
  } catch (error) {
    console.error('Error fetching users by client:', error);
    return [];
  }
};

export const fetchUserDetailedById = async (
  id: string,
): Promise<User | null> => {
  try {
    const { data } = await getApolloClient().query({
      query: GET_USER_DETAILED,
      variables: { id },
    });
    return data.getUser;
  } catch (error) {
    console.error('Error fetching user by id:', error);
    return null;
  }
};

export const fetchUsersWorkedForClient = async (
  clientId: string,
): Promise<User[]> => {
  try {
    const { data } = await getApolloClient().query({
      query: GET_WORKERS_BY_CLIENT,
      variables: { clientId },
    });
    return data.getWorkersByClientId;
  } catch (error) {
    console.error('Error fetching users by client:', error);
    return [];
  }
};

export const fetchUserById = async (id: string): Promise<User | null> => {
  try {
    const { data } = await getApolloClient().query({
      query: GET_USER,
      variables: { id },
    });
    return data.getUser;
  } catch (error) {
    console.error('Error fetching user by id:', error);
    return null;
  }
};

export const updateUser = async (
  id: string,
  details: UserInput,
): Promise<User | null> => {
  try {
    const { data } = await getApolloClient().mutate({
      mutation: UPDATE_USER,
      variables: {
        id,
        details,
      },
      refetchQueries: [{ query: GET_CURRENT_USER }],
    });
    return data?.updateUser;
  } catch (error) {
    console.error('Error updating user:', error);
    throw error;
  }
};

export const updateUserStatus = async (
  id: string,
  status: UserStatus,
): Promise<User | null> => {
  try {
    const { data, errors } = await getApolloClient().mutate({
      mutation: UPDATE_USER_STATUS,
      variables: { id, status },
    });

    handleGraphQLErrors(errors);

    return data?.updateUserStatus;
  } catch (error) {
    console.error('Error updating user:', error);
    throw error;
  }
};

export const updateUserAssignedAdmin = async (
  id: string,
  assignedAdminId: string,
  changeReason?: string | null,
): Promise<User | null> => {
  try {
    const { data, errors } = await getApolloClient().mutate({
      mutation: UPDATE_USER_ASSIGNED_ADMIN,
      variables: {
        id,
        assignedAdminId,
        changeReason: changeReason?.trim() || undefined,
      },
    });

    handleGraphQLErrors(errors);

    return data?.updateUserAssignedAdmin;
  } catch (error) {
    console.error('Error updating assigned admin:', error);
    throw error;
  }
};

export const assignClassificationLevelToUser = async (
  userId: string,
  classificationLevelId: string,
): Promise<User | null> => {
  try {
    const { data } = await getApolloClient().mutate({
      mutation: ASSIGN_CLASSIFICATION_LEVEL_TO_USER,
      variables: { userId, classificationLevelId },
    });
    return data?.assignClassificationLevelToUser;
  } catch (error) {
    console.error('Error assigning classification level to user:', error);
    throw error;
  }
};

export const removeClassificationLevelFromUser = async (
  userId: string,
  classificationLevelId: string,
): Promise<User | null> => {
  try {
    const { data } = await getApolloClient().mutate({
      mutation: REMOVE_CLASSIFICATION_LEVEL_FROM_USER,
      variables: { userId, classificationLevelId },
    });
    return data?.removeClassificationLevelFromUser;
  } catch (error) {
    console.error('Error removing classification level from user:', error);
    throw error;
  }
};

export interface InitiateUserSelfDeleteResult {
  success: boolean;
  errorMessage?: string;
}

export const initiateUserSelfDelete = async (
  reason?: string,
): Promise<InitiateUserSelfDeleteResult> => {
  try {
    const { data, errors } = await getApolloClient().mutate({
      mutation: INITIATE_USER_SELF_DELETE,
      variables: { reason },
    });

    if (errors && errors.length > 0) {
      return {
        success: false,
        errorMessage: errors[0].message || 'Failed to delete account',
      };
    }

    return {
      success: data?.initiateUserSelfDeletion ?? false,
    };
  } catch (error: any) {
    console.error('Error initiating user self delete:', error);

    // Extract error message from GraphQL errors
    const graphQLErrors = error?.graphQLErrors || [];
    if (graphQLErrors.length > 0) {
      return {
        success: false,
        errorMessage: graphQLErrors[0].message || 'Failed to delete account',
      };
    }

    return {
      success: false,
      errorMessage: error?.message || 'An unexpected error occurred',
    };
  }
};

/**
 * Clears the authenticated user's FCM device token on the server.
 * Best-effort — call while still authenticated, before sign-out.
 */
export const clearUserFcmToken = async (): Promise<boolean> => {
  try {
    const { data } = await getApolloClient().mutate({
      mutation: CLEAR_USER_FCM_TOKEN,
    });
    return data?.clearUserFCMToken === true;
  } catch (error) {
    console.error('Error clearing user FCM token:', error);
    return false;
  }
};

export const verifyCurrentUserPassword = async (
  password: string,
): Promise<boolean> => {
  try {
    const { data, errors } = await getApolloClient().mutate({
      mutation: VERIFY_CURRENT_USER_PASSWORD,
      variables: { password },
    });

    if (errors && errors.length > 0) {
      return false;
    }

    return data?.verifyCurrentUserPassword === true;
  } catch (error) {
    console.error('Error verifying current user password:', error);
    return false;
  }
};

export const fetchSuperDetailsForCurrentUser = async (): Promise<
  CustomAttribute[]
> => {
  try {
    const { data } = await getApolloClient().query({
      query: GET_SUPER_DETAILS_FOR_CURRENT_USER,
      fetchPolicy: 'network-only',
    });
    return data?.getSuperDetailsForCurrentUser ?? [];
  } catch (error) {
    console.error('Error fetching super details for current user:', error);
    throw error;
  }
};

export const fetchBankAndTaxDetailsForCurrentUser = async (): Promise<
  CustomAttribute[]
> => {
  try {
    const { data } = await getApolloClient().query({
      query: GET_BANK_AND_TAX_DETAILS_FOR_CURRENT_USER,
      fetchPolicy: 'network-only',
    });
    return data?.getBankAndTaxDetailsForCurrentUser ?? [];
  } catch (error) {
    console.error(
      'Error fetching bank and tax details for current user:',
      error,
    );
    throw error;
  }
};

export const userActions = {
  fetchStatsForCurrentUser,
  fetchCurrentUser,
  fetchCurrentUserOnMobile,
  fetchUsersByTenant,
  fetchWorkersListByTenantPaginated,
  fetchTenantUsersForTenant,
  fetchUserById,
  fetchUserDetailedById,
  fetchUsersByClient,
  fetchUsersWorkedForClient,
  updateUser,
  updateUserStatus,
  updateUserAssignedAdmin,
  assignClassificationLevelToUser,
  removeClassificationLevelFromUser,
  initiateUserSelfDelete,
  verifyCurrentUserPassword,
  fetchSuperDetailsForCurrentUser,
  fetchBankAndTaxDetailsForCurrentUser,
};
